import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from "@material-ui/core";
import Title from "./Title";
import { fetchDeliveryDetails, getPostfixCodeFromEvent } from "./utils";
import { DeliveryDetails, EmailFlowLogDetails } from "@dashboard-v3/api";

interface Props {
  event: EmailFlowLogDetails;
  recipient: string;
  smtpDeliveredEvent?: EmailFlowLogDetails;
}

export default function MailDelivered({
  event,
  recipient,
  smtpDeliveredEvent,
}: Props) {
  const { t } = useTranslation("tracing");
  const { enqueueSnackbar } = useSnackbar();
  const [details, setDetails] = useState<DeliveryDetails[]>();
  const [isLoading, setIsLoading] = useState(false);
  const code = getPostfixCodeFromEvent(event);
  const canFetchS3Details = !smtpDeliveredEvent && !!code;
  const showShowMore = canFetchS3Details && !details;
  const showEmptyMessage = canFetchS3Details && details?.length === 0;
  const showS3Details = canFetchS3Details && details?.length > 0;
  const msgUID = event.msgUID || "-";
  const receivedId = (event.data?.receivedId as string) || "-";
  const serverResponse = (event.data?.serverResponse as string) || "-";

  async function loadInfo() {
    setIsLoading(true);
    try {
      const response = await fetchDeliveryDetails({ event, code, recipient });
      setDetails(response);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("common:errors.fetchError"), {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleShowMore(e: MouseEvent) {
    e.preventDefault();
    loadInfo();
  }

  if (!smtpDeliveredEvent && !code) return null;

  return (
    <>
      <Title timestamp={event.createdAt}>
        <Box display="flex" gridGap="1rem" alignItems="baseline">
          <Typography style={{ fontWeight: "500" }}>
            {t(`eventActionType.${event.action}`)}
          </Typography>
          {showShowMore && (
            <>
              <Link href="#" onClick={handleShowMore}>
                {t("delivered.showMore")}
              </Link>
              {isLoading && <CircularProgress size="1rem" color="inherit" />}
            </>
          )}
        </Box>
      </Title>

      <Box ml={2}>
        <Grid container direction="row" justifyContent="flex-start" spacing={3}>
          <Grid item>
            <Property title="msgUID" value={msgUID} />
          </Grid>
          <Grid item>
            <Property title="receivedId" value={receivedId} />
          </Grid>
          <Grid item xs={12}>
            <Property
              title={t("delivered.serverResponse")}
              value={serverResponse}
            />
          </Grid>
        </Grid>
      </Box>

      {showEmptyMessage && <Typography>{t("delivered.empty")}</Typography>}

      {showS3Details &&
        details.map((item, index) => (
          <Box key={item.time} mt={2} ml={2} width="100%">
            <Typography
              variant="caption"
              style={{ fontSize: 15, fontWeight: 500 }}
            >
              {t("delivered.deliveryDetails")}
            </Typography>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              spacing={3}
              style={{ marginTop: 2, marginLeft: 10, marginBottom: 2 }}
            >
              <Grid item>
                <Property
                  title={t("delivered.at")}
                  value={new Date(item.time).toLocaleString()}
                />
              </Grid>
              <Grid item>
                <Property
                  title={t("delivered.delay")}
                  value={t("delivered.delaySeconds", { count: item.delay })}
                />
              </Grid>
              <Grid item>
                <Property title={t("delivered.relay")} value={item.relay} />
              </Grid>
              <Grid item xs={12}>
                <Property title={t("delivered.status")} value={item.status} />
              </Grid>
            </Grid>
            {index < details.length - 1 && (
              <Box my={2}>
                <Divider light />
              </Box>
            )}
          </Box>
        ))}
    </>
  );
}

function Property({ value, title }: { value: string; title: string }) {
  return (
    <Box display="flex" flexDirection="column">
      <Typography color="textSecondary" component="span" variant="caption">
        {title}
      </Typography>
      <Box>{value}</Box>
    </Box>
  );
}
